import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { AGENTS } from '@/data/agents';
import { PERSONA_CARDS } from '@/data/personas';
import { selfPersonIdFromSession } from '@/utils/commsHelpers';
import type { Persona } from '@/domain/types';

/**
 * Who did it (14ym9bv75ky). Audit rows ("Requested by", "Resolved by",
 * reschedule history, ledger "Sent by", checkout) record the signed-in PERSON,
 * never the persona/role slug. Flows default their `by` to currentActorId();
 * screens render any stored id through actorLabel().
 *
 * Old rows are NOT backfilled: they keep their slug and actorLabel reads them
 * through the legacy labels below.
 */

/**
 * The signed-in person's id: the agent id for Ops seats, the artist/apprentice
 * id for chair seats (selfPersonIdFromSession). The seats with no agent record
 * behind them (messenger, finance, finance-manager) are one person each, so the
 * seat id names them (actorLabel resolves it through the persona card).
 * Undefined when nobody is signed in.
 */
export function currentActorId(): string | undefined {
  const s = useSessionStore.getState();
  if (!s.persona) return undefined;
  return (
    selfPersonIdFromSession({
      persona: s.persona,
      agentId: s.agentId,
      artistId: s.artistId,
      apprenticeId: s.apprenticeId,
    }) ?? s.persona
  );
}

/**
 * currentActorId() only when it is a real staff agent. For columns that
 * reference app.agents (bookings.checked_out_by), where an artist or finance
 * seat id would break the foreign key.
 */
export function currentStaffAgentId(): string | undefined {
  const id = currentActorId();
  if (!id) return undefined;
  return useBrainStore.getState().agents[id] || AGENTS[id] ? id : undefined;
}

/** Labels for the role slugs older rows stored before 14ym9bv75ky, and for the system actors. */
const LEGACY_LABEL: Record<string, string> = {
  'studio-manager': 'Studio manager',
  'bookings-agent': 'Bookings agent',
  agent: 'Agent',
  artist: 'Artist',
  apprentice: 'Apprentice',
  'front-desk': 'Front desk',
  system: 'System',
  Console: 'Console',
  // Not people: the actors money and texts are recorded under (7frb). A Stripe
  // link payment is 'stripe' (client + server), a client's own top-up on the pay
  // page is 'customer', a cadence text is 'auto-studio'.
  stripe: 'Stripe',
  customer: 'Client',
  Client: 'Client',
  'auto-studio': 'Automatic text',
};

/** A stored actor id as "First Last"; legacy slugs keep a readable role label. */
export function actorLabel(id: string | null | undefined): string {
  if (!id) return 'not recorded';
  const brain = useBrainStore.getState();
  const agent = brain.agents[id] ?? AGENTS[id];
  if (agent) return `${agent.firstName} ${agent.lastName}`.trim();
  const artist = brain.artists[id];
  if (artist) return artist.displayName || `${artist.firstName} ${artist.lastName}`.trim();
  const legacy = LEGACY_LABEL[id];
  if (legacy) return legacy;
  // A seat with no agent record (messenger / finance / finance-manager).
  const card = PERSONA_CARDS.find((c) => c.persona === id && !c.agentId && !c.artistId && !c.apprenticeId);
  if (card) return card.name;
  return id;
}

/** The signed-in person as an audit actor: stable id, "First Last", and seat. */
export interface Actor {
  id: string;
  name: string;
  persona: Persona | null;
}

/**
 * The signed-in person for an audit row that stores BOTH the id and the name
 * (reconciliation "Resolved by" / "Approved by", W7 n3). The finance seats read
 * as their people ("Robin Ashby" / "Dana Whitlock"), never the role slug.
 */
export function currentActor(): Actor {
  const persona = useSessionStore.getState().persona;
  const id = currentActorId() ?? 'unknown';
  return { id, name: id === 'unknown' ? 'Unknown' : actorLabel(id), persona };
}
